import { useEffect, useRef } from 'react';
import { message } from 'antd';
import useScanBufferStore from '../store/scanBufferStore';
import useScanTargetStore from '../store/scanTargetStore';
import { markingApi } from '../api/marking';

function getScanKey(scan: { marking_code: string; scanned_at: string }): string {
  return `${scan.marking_code}|${scan.scanned_at}`;
}

/**
 * Хук автодобавления сканов из буфера в открытый заказ-наряд.
 * Подписывается на изменения буфера и при появлении новых марок
 * проверяет через backend, можно ли добавить, и добавляет если включено.
 */
export default function useAutoAddBuffer() {
  const prevItemsRef = useRef<Set<string>>(new Set());
  const processedRef = useRef<Map<string, string>>(new Map()); // marking_code -> scanned_at
  const lastTargetKeyRef = useRef<string | null>(null);
  // rev. 2026-08-19: базовая линия формируется только после догрузки буфера —
  // иначе старые сканы принимались за новые (ложный warning «уже в заказе»)
  const awaitingBaselineRef = useRef(false);

  const items = useScanBufferStore((s) => s.items);
  const loading = useScanBufferStore((s) => s.loading);
  const targetOrderId = useScanTargetStore((s) => s.orderId);
  const targetHandlerRegistered = useScanTargetStore((s) => s.handler !== null);
  // rev. 2026-09-19 (инцидент: автодобавление всего буфера при обновлении новой
  // заявки): ключ цели — регистрация handler + orderId. У НОВОГО заказа
  // orderId = null, и открытие страницы не считалось сменой цели (null→null) —
  // базовая линия не взводилась, весь буфер улетал в черновик после догрузки
  const targetKey = targetHandlerRegistered ? String(targetOrderId ?? 'new') : null;

  useEffect(() => {
    // При активации/смене целевого заказа — переснять базовую линию
    if (targetKey !== lastTargetKeyRef.current) {
      lastTargetKeyRef.current = targetKey;
      awaitingBaselineRef.current = targetKey !== null;
    }

    // Базовая линия: ждём окончания догрузки буфера, затем помечаем
    // все текущие сканы обработанными — старые сканы не автодобавляются
    if (awaitingBaselineRef.current) {
      if (loading) return;
      const currentKeys = new Set(items.map(getScanKey));
      prevItemsRef.current = currentKeys;
      const newProcessed = new Map<string, string>();
      items.forEach((s) => {
        newProcessed.set(s.marking_code, s.scanned_at);
      });
      processedRef.current = newProcessed;
      awaitingBaselineRef.current = false;
      return;
    }

    const currentKeys = new Set(items.map(getScanKey));

    // Находим только НОВЫЕ сканы (которых не было в prevItemsRef)
    const newItems = items.filter((s) => !prevItemsRef.current.has(getScanKey(s)));

    if (newItems.length === 0) {
      prevItemsRef.current = currentKeys;
      return;
    }

    const {
      handler: targetHandler,
      orderNumber: targetOrderNumber,
    } = useScanTargetStore.getState();

    const autoAddEnabled = (() => {
      try {
        const saved = localStorage.getItem('scanBufferAutoAdd');
        return saved === null ? true : saved === 'true';
      } catch {
        return true;
      }
    })();

    // rev. 2026-09-18: scan-target бывает и у НОВОГО (несохранённого) заказа —
    // orderId тогда null: backend-проверки (checkCanAdd/resolveCode) пропускаем,
    // resolveCode выполняется страницей после первого сохранения.
    if (!autoAddEnabled || !targetHandler) {
      prevItemsRef.current = currentKeys;
      return;
    }

    newItems.forEach(async (scan) => {
      // rev. 2026-08-10: bulk-марки не автодобавляются — разливной товар идёт в ЗН по номенклатуре
      if (scan.product_type === 'bulk') {
        processedRef.current.set(scan.marking_code, scan.scanned_at);
        return;
      }

      // Проверка кеша: уже обрабатывали и scanned_at не изменился
      const lastProcessed = processedRef.current.get(scan.marking_code);
      if (lastProcessed && lastProcessed === scan.scanned_at) {
        return;
      }

      // Проверка через backend: можно ли добавить марку в заказ
      // (только для сохранённого заказа — у нового заказа проверять нечего)
      if (targetOrderId != null) {
        try {
          const check = await markingApi.checkCanAdd(scan.marking_code, targetOrderId);
          if (!check.data.can_add) {
            message.warning(check.data.reason || 'Марку нельзя добавить в заказ');
            processedRef.current.set(scan.marking_code, scan.scanned_at);
            return;
          }
        } catch (e: any) {
          message.error(e?.response?.data?.detail || 'Ошибка проверки марки');
          return;
        }
      }

      try {
        const res = await targetHandler({ ...scan });
        if (res.ok) {
          if (targetOrderId != null) {
            try {
              await markingApi.resolveCode(scan.marking_code, {
                action: 'sale',
                order_id: targetOrderId,
                order_number: targetOrderNumber,
              });
            } catch (e: any) {
              if (e?.response?.status === 409) {
                message.warning('Марка уже не в буфере — возможно, она уже добавлена');
              } else {
                throw e;
              }
            }
          }
          processedRef.current.set(scan.marking_code, scan.scanned_at);
          const targetLabel = targetOrderNumber ? `ЗН № ${targetOrderNumber}` : 'новый заказ';
          if (res.duplicate) {
            message.warning(res.message || `Товар уже в ${targetLabel} — добавлен дубль`);
          } else {
            message.success(res.message || `Добавлено в ${targetLabel}`);
          }
        } else if (res.duplicate) {
          message.warning(res.message || 'Эта бутылка уже добавлена в заказ-наряд');
        } else {
          message.error(res.message || 'Не удалось добавить в заказ');
        }
      } catch (e: any) {
        if (e?.response?.status === 409) {
          message.warning('Марка уже не в буфере — возможно, она уже добавлена');
        } else {
          message.error(e?.response?.data?.detail || 'Ошибка автодобавления в заказ');
        }
      }
    });

    prevItemsRef.current = currentKeys;
  }, [items, loading, targetOrderId, targetKey]);
}
